/*
 * IL GENERATORE DEI TOKEN — da marca e accento, tutto il resto.
 *
 * Portato da Lares (`WEB/app/utility/theme/utils-theme-tokens.js`) il 05-09-2026. Il file
 * originale e' **1.087 righe senza un solo `import`**: colorimetria pura, nessuna dipendenza da
 * Ember, quindi il porto e' quasi una copia — via gli alias di Bootstrap, che sono di ArchitectUI
 * e qui non esistono, e i tipi che `strict: true` pretende.
 *
 * ## Perche' esiste, e cosa risolve che una tavolozza scritta a mano non risolve
 *
 * ⚠️ Prima di questo file `applyUiTheme` scriveva **cinque** variabili
 * (`--mantine-color-brand-*`, `--app-header-bg/fg`, `--app-sidebar-*`), e **nessuna** era un
 * `--*`. Un'azienda che sceglieva il verde otteneva bottoni verdi, testata verde e menu verde
 * — e **marca** in tutte le migliaia di letture dipinte a mano. Il white-label si fermava alle
 * primitive di Mantine, e nessuno lo dichiarava.
 *
 * ⚠️ E la seconda cosa, che vale piu' della prima: il generatore **cerca** il contrasto invece di
 * sperarlo. `cercaContrasto(partenza, fondo, 4.5)` scurisce finche' la soglia non e' raggiunta —
 * ed e' il motivo per cui in Lares un link a 3,73:1 come `#5B89AE` **non puo' nascere**. Non e'
 * stato corretto: e' irraggiungibile per costruzione.
 *
 * ## La regola che tiene tutto
 *
 * ⚠️ Si salvano **le scelte** (marca, accento, modo), mai i token calcolati. Una sola fonte, la
 * formula. E' la lezione delle cinque righe su sette in cui Theme Studio e `tokens.css`
 * divergevano: due copie dello stesso valore divergono sempre, e la seconda nessuno la ricontrolla.
 */

export type Mode = 'light' | 'dark';
export interface Rgb { r: number; g: number; b: number }
export interface Rgba extends Rgb { a: number }
export interface Hsl { h: number; s: number; l: number }
export interface Adaptation {
  l?: number | null;
  lMul?: number | null;
  s?: number | null;
  sMul?: number | null;
}
export type Token = Record<string, string>;
export interface Check {
  nome: string;
  report: number;
  threshold: number;
  outcome: string;
}


// Soglie WCAG 2.2. Il testo normale vuole 4,5:1 (1.4.3); i contorni dei
// controlli, gli indicatori di stato e l'anello di focus vogliono 3:1
// (1.4.11 e 2.4.11). Il testo grande si accontenta di 3:1, ma noi non lo
// sfruttiamo: un token non sa in che corpo verrà usato.
/**
 * Quanto e' carica la barra dell'applicazione, in luminosita' HSL.
 *
 * E' l'unico numero da toccare se la testata sembra troppo pallida o troppo
 * carica: la tinta la da' l'accento dell'azienda, questa e' solo la profondita'.
 *
 * Il valore di partenza dell'handoff era `#E9F0F7`, cioe' **94**, e a occhio
 * sparisce contro il bianco del menu (1,15:1). A **86** lo stacco quasi raddoppia (1,39:1)
 * e la barra si legge come una zona invece che come un'ombra, restando una
 * velatura e non una fascia colorata: MaeDesign §3 vieta i fondi di *marca*
 * pieni oltre i 400x400px, e qui siamo su una velatura di *accento*.
 *
 * La leggibilita' non e' il vincolo — l'inchiostro resta sopra 8:1 fino a 78 —
 * quindi la scala e' tutta percorribile:
 *
 *     94  #E8F1F7   testo 12,06:1   stacco dal menu 1,14:1   (era questo)
 *     90  #D9E7F2   testo 10,95:1   stacco 1,26:1
 *     86  #C9DEED   testo  9,96:1   stacco 1,39:1   ← in uso
 *     82  #BAD4E8   testo  8,98:1   stacco 1,54:1
 *     78  #ABCAE3   testo  8,08:1   stacco 1,71:1
 */
export const APP_BAR_BRIGHTNESS = 86;

/**
 * La luminosita' della barra dell'applicazione in modo **scuro**.
 *
 * Qui c'era `#191D25`, un grigio-blu fisso: passando a scuro la testata perdeva
 * il colore dell'azienda e diventava quasi nera, uguale per tutti. Ma il modo
 * scuro cambia il **fondo**, non l'identita': la barra e' il posto dove
 * un'azienda si riconosce, e non c'e' motivo perche' smetta di riconoscersi la
 * sera.
 *
 * 14 su 100 e' scuro quanto quel grigio — il testo chiaro ci sta sopra con lo
 * stesso agio — ma **tinto**: chi ha scelto il verde vede un antracite verde,
 * chi ha scelto l'ambra un antracite caldo. La tinta si legge, la luminosita'
 * resta quella di prima.
 */
export const DARK_APP_BAR_BRIGHTNESS = 14;

/**
 * Quanto puo' essere carica la barra dell'applicazione, in saturazione HSL.
 *
 * Senza tetto la barra eredita la saturazione dell'accento, e questo funziona
 * solo finche' l'accento e' poco carico. L'azzurro Vento sta a **51%** e
 * l'originale di MaeDesign (`#E9F0F7`) a 47%: era una coincidenza, non una
 * regola. Un'azienda che sceglie un rosso a **85%** otteneva `#FABDBD`, cioe'
 * rosa confetto su tutta la larghezza dello schermo.
 *
 * Il tetto separa i due significati: la sorgente dice **quale tinta**, la barra
 * decide **quanto carica**. Cosi' una tinta qualunque produce una superficie
 * calma, e la famiglia di colore resta riconoscibile:
 *
 *     accento             senza tetto   con tetto 30
 *     azzurro  s=51%      #C9DEED       #D1DDE6   (resta azzurro)
 *     rosso    s=85%      #FABDBD       #E6D1D1   (da rosa a grigio caldo)
 *     ciano    s=87%      #BCEEFA       #D1E2E6
 *     ambra    s=65%      #F2DDC4       #E6DCD1
 *
 * L'inchiostro resta intorno a 10:1 su tutte.
 */
export const APP_BAR_SATURATION = 30;

export const TEXT_THRESHOLD = 4.5;
export const NON_TEXT_THRESHOLD = 3;

// ---------------------------------------------------------------------------
// Conversioni
// ---------------------------------------------------------------------------

/** `#abc` e `#aabbcc` → `{r, g, b}`. Lancia se non è un esadecimale. */
export function hexToRgb(hex: string): Rgb {
  const clean = String(hex).trim().replace(/^#/, '');
  const extended =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;

  if (!/^[0-9a-fA-F]{6}$/.test(extended)) {
    throw new Error(`Colore non valido: "${hex}"`);
  }

  return {
    r: parseInt(extended.slice(0, 2), 16),
    g: parseInt(extended.slice(2, 4), 16),
    b: parseInt(extended.slice(4, 6), 16),
  };
}

/** `{r, g, b}` → `#AABBCC`. I valori fuori scala vengono riportati dentro. */
export function rgbToHex({ r, g, b }: Rgb): string {
  const channel = (v: number) =>
    Math.round(Math.max(0, Math.min(255, v)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`.toUpperCase();
}

/** `{r, g, b}` → `{h: 0–360, s: 0–100, l: 0–100}`. */
export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) return { h: 0, s: 0, l: l * 100 };

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;

  return { h: (h / 6) * 360, s: s * 100, l: l * 100 };
}

/** `{h, s, l}` → `#AABBCC`. */
export function hslToHex({ h, s, l }: Hsl): string {
  const hn = (((h % 360) + 360) % 360) / 360;
  const sn = Math.max(0, Math.min(100, s)) / 100;
  const ln = Math.max(0, Math.min(100, l)) / 100;

  if (sn === 0) {
    const v = ln * 255;
    return rgbToHex({ r: v, g: v, b: v });
  }

  const q = ln < 0.5 ? ln * (1 + sn) : ln + sn - ln * sn;
  const p = 2 * ln - q;
  const canale = (t: number) => {
    const tt = (t + 1) % 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };

  return rgbToHex({
    r: canale(hn + 1 / 3) * 255,
    g: canale(hn) * 255,
    b: canale(hn - 1 / 3) * 255,
  });
}

/**
 * Compone un colore trasparente sul suo fondo e restituisce il colore pieno
 * equivalente.
 *
 * Serve perché **il contrasto di una velatura non si misura sulla velatura**:
 * `rgba(255,255,255,0.10)` su `#1A1D24` non è "bianco al 10%", è `#31343A`, e
 * vale 1,35:1 contro la superficie. Senza questo passaggio un bordo che sembra
 * chiaro risulta conforme sulla carta e invisibile sullo schermo.
 */
export function composeOnBackground({ r, g, b, a }: Rgba, background: string): string {
  const f = hexToRgb(background);
  return rgbToHex({
    r: r * a + f.r * (1 - a),
    g: g * a + f.g * (1 - a),
    b: b * a + f.b * (1 - a),
  });
}

// ---------------------------------------------------------------------------
// Contrasto
// ---------------------------------------------------------------------------

/** Luminanza relativa secondo WCAG 2.x. */
export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const linear = (v: number) => {
    const n = v / 255;
    return n <= 0.03928 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/**
 * Rapporto di contrasto fra due colori pieni, da 1 a 21.
 *
 * ⚠️ È la formula di WCAG 2.x, ed è nota per trattare male il testo chiaro su
 * fondo scuro: un numero che passa in dark non garantisce che si legga bene.
 * L'algoritmo candidato a sostituirla (APCA, in lavorazione per WCAG 3) non è
 * ancora normativo, quindi la soglia di legge resta questa — ma in dark vale la
 * pena guardare anche il risultato, non solo il numero.
 */
export function contrast(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** `'aaa' | 'aa' | 'fail'` per una coppia, data la soglia richiesta. */
export function judgement(report: number, threshold: number = TEXT_THRESHOLD): string {
  if (threshold === NON_TEXT_THRESHOLD) return report >= 3 ? 'aa' : 'fail';
  if (report >= 7) return 'aaa';
  if (report >= threshold) return 'aa';
  return 'fail';
}

// ---------------------------------------------------------------------------
// Derivazione
// ---------------------------------------------------------------------------

/** Sposta luminosità e saturazione tenendo ferma la tinta. */
export function adapt(hex: string, { l = null, lMul = null, s = null, sMul = null }: Adaptation): string {
  const c = rgbToHsl(hexToRgb(hex));
  let L = c.l;
  let S = c.s;
  if (lMul != null) L *= lMul;
  if (l != null) L = l;
  if (sMul != null) S *= sMul;
  if (s != null) S = s;
  return hslToHex({ h: c.h, s: S, l: L });
}

/**
 * Il colore più vicino a `partenza` che raggiunge `soglia` contro `fondo` — che può essere
 * **uno o più** fondi, e in quel caso vince il peggiore — a tinta e saturazione invariate.
 *
 * ⚠️ **La firma è plurale dal 30-09-2026, e non è una comodità: è la riparazione di un difetto
 * che si era già ripetuto tre volte.** Cercare contro un fondo solo produce un colore che sta
 * appena sopra soglia **lì** e sotto ovunque altrove — e siccome il risultato è «conforme» a
 * chi lo misura sullo stesso fondo su cui è stato cercato, la verifica conferma l'errore invece
 * di trovarlo. È successo a `accent-text` (05-09: 4,54 sulla superficie, 4,14 sul proprio
 * tenue), a `ink-300` (29-09: 4,51 su bianco, 4,17 su `surface-2`) e a `brand-text`, dove su 72
 * colori di marca provati **49** producevano un testo sotto soglia sul proprio tenue.
 *
 * Passare un fondo solo resta legittimo dove il fondo **è** uno solo — il testo sopra un
 * riempimento pieno, per dire. Dove l'inchiostro gira per l'applicazione, si passano tutti.
 *
 * È il cuore della correzione: dove c'è un obbligo di contrasto non si applica
 * un moltiplicatore, si cerca. La direzione la decide il fondo — su fondo
 * chiaro si scurisce, su fondo scuro si schiarisce — e il passo è di un quarto
 * di punto di luminosità, abbastanza fine da non far scattare oltre il
 * necessario.
 *
 * Se nessuna luminosità basta (capita con accenti molto saturi su fondi
 * intermedi) restituisce l'estremo raggiunto: meglio il massimo possibile che
 * un'eccezione che spegne il tema.
 */
export function searchContrast(
  departure: string,
  fondo: string | readonly string[],
  soglia: number
): string {
  const backgrounds = typeof fondo === 'string' ? [fondo] : fondo;
  /* Il fondo **peggiore** decide, ed è tutto il punto della firma plurale: un inchiostro cercato
     contro uno solo passa lì e cade altrove. */
  const worst = (ink: string): number =>
    Math.min(...backgrounds.map((b) => contrast(ink, b)));

  if (worst(departure) >= soglia) return departure;

  const c = rgbToHsl(hexToRgb(departure));
  /* La direzione la decide il fondo più chiaro del gruppo: se il testo deve scurire per stare su
     bianco, scurire gli va bene anche sul resto. Un gruppo con fondi sui due lati non ha una
     direzione sola, e nessun colore lo risolverebbe — per questo i chiamanti passano fondi
     omogenei (tutti chiari o tutti scuri). */
  const brightest = backgrounds.reduce((a, b) =>
    relativeLuminance(a) >= relativeLuminance(b) ? a : b
  );
  const downwards = relativeLuminance(brightest) > 0.18;
  const step = downwards ? -0.25 : 0.25;

  let last = departure;
  for (let L = c.l; L >= 0 && L <= 100; L += step) {
    const candidate = hslToHex({ h: c.h, s: c.s, l: L });
    last = candidate;
    if (worst(candidate) >= soglia) return candidate;
  }
  return last;
}

/**
 * Il riempimento di marca su fondo scuro.
 *
 * Il bordeaux ufficiale `#540409` ha luminosità quasi identica al fondo scuro:
 * come superficie piena **affonda** (1,2:1) e come testo è illeggibile. Serve
 * un colore che soddisfi **due vincoli insieme** — il bianco sopra deve leggersi
 * (4,5:1) e il riempimento deve staccarsi dalla superficie (3:1) — e nessun
 * moltiplicatore li rispetta entrambi per costruzione.
 *
 * Questa è la funzione che MaeDesign ha fatto giusta, e la teniamo com'è: scorre
 * la finestra di luminosità utile e si ferma alla prima che passa entrambe. Se
 * nessuna passa, restituisce quella col punteggio migliore invece di arrendersi.
 */
export function brandFillOnDark(brand: string, surface: string): string {
  const c = rgbToHsl(hexToRgb(brand));

  // Due gradi di libertà, non uno. Con la sola luminosità un verde saturo non
  // trova soluzione — il bianco vuole che scenda, lo stacco dalla superficie
  // vuole che salga — e si finiva sul ripiego non conforme. Abbassare anche la
  // saturazione allarga la finestra: un colore meno carico regge il bianco a
  // parità di luminosità.
  let best = null;
  let bestScore = -1;

  for (const S of [Math.min(c.s, 58), 48, 38, 28]) {
    for (let L = 30; L <= 70; L += 1) {
      const candidato = hslToHex({ h: c.h, s: S, l: L });
      const withWhite = contrast('#FFFFFF', candidato);
      const gap = contrast(candidato, surface);

      if (withWhite >= TEXT_THRESHOLD && gap >= NON_TEXT_THRESHOLD)
        return candidato;

      // Il ripiego non pesa i due vincoli allo stesso modo. La regola cardine
      // del Brand Manual è «massimo contrasto testo/sfondo, sempre leggibile»,
      // e vince su ogni altra: se proprio si deve cedere, si cede sullo stacco
      // dalla superficie — un bottone poco staccato è brutto, un bottone con
      // l'etichetta illeggibile è rotto.
      const score =
        2 * Math.min(withWhite / TEXT_THRESHOLD, 1) +
        Math.min(gap / NON_TEXT_THRESHOLD, 1);
      if (score > bestScore) {
        bestScore = score;
        best = candidato;
      }
    }
  }
  /*
   * ⚠️ `?? marca` non è un caso che succede: il ciclo valuta quattro saturazioni per una fascia
   * di luminosità, e il primo candidato batte sempre `punteggioMigliore = -1`, quindi `migliore`
   * esce assegnato. È la chiusura del **tipo**, non un ripiego di comportamento — nel porto a
   * TypeScript vale scriverla invece di propagare un `null` per dieci firme.
   */
  return best ?? brand;
}

// ---------------------------------------------------------------------------
// I neutri, fissi per modo
// ---------------------------------------------------------------------------

/**
 * I neutri non derivano dai due colori: sono le superfici e gli inchiostri, e
 * cambiano solo fra chiaro e scuro. Vengono da MaeDesign §1.2 e §5, **con due
 * correzioni** che l'audit del 26/08 ha reso obbligatorie:
 *
 * - `ink-300` (il placeholder) era `#9CA0A8`, cioè 2,62:1 su bianco. Un
 *   placeholder è testo che l'utente deve leggere per sapere cosa scrivere: la
 *   deroga WCAG vale per i controlli disattivati, non per un campo attivo e
 *   vuoto. ⚠️ **29-09-2026**: `#717781` era misurato su bianco e basta — su
 *   `surface-2` fa 4,17 e sul tenue di marca 3,82. Ora `#686B70` e, in scuro,
 *   `#888D98`: passano su ogni fondo dei due temi. Il foglio statico intanto
 *   teneva ancora `#9CA0A8` (2,62:1), quindi i due divergevano.
 * - `line-strong` **non esisteva**. `line` su bianco vale 1,25:1 contro i 3
 *   richiesti per il contorno di un controllo (1.4.11): un ipovedente non trova
 *   dove comincia il campo. Il token originale resta per i divisori, dove la
 *   leggerezza è voluta e nessuna norma la vieta; i bordi dei campi usano
 *   questo.
 */
const LIGHT_NEUTRAL = {
  'ink-900': '#0F1115',
  'ink-700': '#2A2D33',
  'ink-500': '#5A5E66',
  'ink-300': '#66696E',
  line: '#E5E6EA',
  'line-strong': '#9094A5',
  bg: '#FFFFFF',
  surface: '#FFFFFF',
  'surface-2': '#F5F6F8',
};

const DARK_NEUTRAL = {
  'ink-900': '#FFFFFF',
  'ink-700': '#C9CCD2',
  'ink-500': '#9CA0A8',
  'ink-300': '#888D98',
  line: 'rgba(255,255,255,0.10)',
  // Al 10% il bordo vale 1,35:1: serve il 33% per arrivare a 3:1.
  'line-strong': 'rgba(255,255,255,0.33)',
  bg: '#0F1115',
  surface: '#1A1D24',
  'surface-2': '#22262E',
};

/**
 * I colori di stato, con le correzioni dell'audit.
 *
 * In chiaro verde e giallo non passavano né sul bianco né — soprattutto —
 * dentro i propri sfondi tenui, che è il posto dove per convenzione di progetto
 * vanno gli avvisi in linea accanto ai campi. I valori scelti passano **nel caso
 * peggiore dei due**, cioè dentro lo sfondo tenue.
 */
const LIGHT_STATUS = {
  success: '#1B7C4E', // era #1E8B57: 3,75:1 dentro success-bg
  'success-bg': '#DEF5E8',
  warning: '#9A6006', // era #B97307: 3,34:1 dentro warning-bg
  'warning-bg': '#FCEFD3',
  danger: '#B91C1C',
  'danger-bg': '#FBE3E3',
};

const DARK_STATUS = {
  success: '#34C48B',
  'success-bg': 'rgba(30,139,87,0.18)',
  warning: '#E0A33B',
  'warning-bg': 'rgba(185,115,7,0.18)',
  danger: '#E36565',
  'danger-bg': 'rgba(185,28,28,0.18)',
};

/**
 * Le serie dei grafici.
 *
 * Restano **i tre colori di MaeDesign, invariati**, ed è una decisione
 * deliberata. La serie 1 e la serie 3 valgono 1,75:1 fra loro in chiaro e
 * 1,40:1 in scuro: adiacenti in una barra impilata non si distinguono. Ma la
 * correzione cromatica non esiste — per portare la serie 3 a 3:1 dalla serie 1
 * servirebbe `#133B5D`, che in scuro scende a 1,58:1 contro la superficie e
 * sparisce; e quattro tinte alternative provate (ocra, oliva, viola,
 * verde-acqua) stanno tutte nella stessa fascia di luminanza, fra 1,52 e 1,86.
 *
 * Con tre colori derivati da due tinte di marca il problema **non ha soluzione
 * di tavolozza**, ed è giusto così: la tavolozza serve l'identità. Si risolve
 * disegnando il confine — 2px di separatore del colore della superficie fra
 * segmenti adiacenti, ed etichette dirette sul grafico — che è la stessa regola
 * che MaeDesign già applica ai colori di stato («sempre con icona + label, mai
 * solo colore»). Quella regola vive in chi disegna il grafico, non qui.
 */
const LIGHT_CHARTS = {
  'chart-1': '#4A87BD',
  'chart-2': '#9E3A41',
  'chart-3': '#1F5F96',
};
const DARK_CHARTS = {
  'chart-1': '#4E8CC8',
  'chart-2': '#C56A71',
  'chart-3': '#3873AD',
};

/**
 * I colori d'identita': «questo progetto», «questa persona», «questa fase».
 *
 * Sono un mestiere **diverso** dalle serie dei grafici, e per questo non sono
 * `--chart-4` e seguenti. Una serie va confrontata con la serie accanto in una
 * barra impilata, e il commento sopra dimostra che con tre tinte derivate da
 * due colori di marca quel confronto non ha soluzione di tavolozza. Un colore
 * d'identita' si vede invece **da solo** sul fondo della pagina: il vincolo che
 * conta e' uno, staccarsi da quel fondo, e quello si puo' garantire.
 *
 * ⚠️ La famiglia nasce da un buco, ed e' il caso di scuola della regola che su
 * Lares porta il numero 26: `--chart-*` aveva **tre** gradini e i punti che li
 * usavano ne volevano **sette**. Nessuno ha aperto un dibattito — ognuno ha
 * riempito il buco col piu' vicino, e il risultato erano array che alternavano
 * token e letterali nella stessa riga:
 *
 *   ['var(--brand)', '#5B89AE', '#8A5A2B', 'var(--chart-3)', '#6B7A3A', 'var(--chart-2)']
 *
 * `#5B89AE` compariva in **cinque** file come «il secondo colore», copiato ogni
 * volta. Un buco in una tavolozza si riempie sempre col token sbagliato piu'
 * vicino: la cura non e' la disciplina di chi scrive, e' chiudere il buco.
 *
 * ⚠️ E il buco costava davvero, in un modo che si vedeva **in un tema solo**.
 * Un letterale non ha variante scura per costruzione, quindi cinque di quelle
 * dodici tinte cadevano sotto i 3:1: `#8A5A2B` a 2,87, `#a82230` a 2,36,
 * `#3c5f76` a 2,48, `#7A1015` a 1,54, e — il peggiore — `var(--brand)`
 * `#540409` a **1,12:1** sul fondo scuro. Il primo gradino, cioe' il piu'
 * frequente: il filo del progetto di marca semplicemente non c'era.
 *
 * ⚠️ Il primo gradino **non e' un valore nuovo**: e' `brand-fill`, che e' il
 * token il cui mestiere e' esattamente «la marca come pieno» ed e' gia' tarato
 * per tema (`#540409` in chiaro, `#C1333C` in scuro). Cosi' il progetto di
 * marca prende il colore dell'azienda, che e' l'intenzione vera, invece di un
 * bordeaux cablato. Tenendo tinta e saturazione, portare `#540409` a 3,3:1 sul
 * fondo scuro darebbe `#DD0B18`: un rosso urlato, cioe' la prova che il valore
 * giusto non si ottiene correggendo il letterale.
 *
 * Gli altri sei tengono l'aspetto che avevano — sono le tinte che qualcuno ha
 * scelto a occhio, e cambiarle senza motivo cambierebbe l'aspetto del prodotto
 * per niente. Due sono corretti nel solo tema in cui cadevano: terra
 * `#8A5A2B → #97632F` e petrolio `#3C5F76 → #48738E`, entrambi in scuro.
 *
 * ⚠️ Quel che questa famiglia **non** promette e' la distinguibilita' fra
 * gradini adiacenti, ed e' deliberato: il commento delle serie ha gia' misurato
 * che non e' ottenibile. Chi mette due colori d'identita' a contatto deve
 * disegnare il confine — un separatore del colore della superficie — o mettere
 * l'etichetta. Promettere qui i 3:1 mutui vorrebbe dire scrivere una garanzia
 * che la tavolozza non puo' mantenere.
 */
const LIGHT_CATEGORIES = {
  'cat-2': '#5B89AE', // azzurro   3,73:1
  'cat-3': '#8A5A2B', // terra     5,87:1
  'cat-4': '#1F5F96', // blu       6,70:1
  'cat-5': '#6B7A3A', // oliva     4,70:1
  'cat-6': '#9E3A41', // mattone   6,70:1
  'cat-7': '#3C5F76', // petrolio  6,79:1
};
const DARK_CATEGORIES = {
  'cat-2': '#5B89AE', // azzurro   4,53:1
  'cat-3': '#97632F', // terra     3,33:1  (era #8A5A2B, 2,87)
  'cat-4': '#3873AD', // blu       3,39:1
  'cat-5': '#6B7A3A', // oliva     3,59:1
  'cat-6': '#C56A71', // mattone   4,56:1
  'cat-7': '#48738E', // petrolio  3,31:1  (era #3C5F76, 2,48)
};

/** Forma, tipografia, spaziatura, movimento: non dipendono dai due colori. */
const SHAPE = {
  'font-base': "'IBM Plex Sans', system-ui, sans-serif",
  'radius-chip': '12px',
  'radius-card': '16px',
  'radius-hero': '22px',
  gutter: '1.5rem',
  'pad-card': '1.5rem',
  // La scala di spaziatura è in `rem`, i raggi restano in `px`.
  //
  // Sono due decisioni opposte e volute. Lo spazio deve crescere col testo: chi
  // porta il carattere del browser a 24px ottiene margini proporzionati invece
  // di righe fitte dentro scatole rimaste piccole (WCAG 1.4.4). Il raggio no —
  // un angolo arrotondato non è più leggibile se cresce, è solo più tondo.
  //
  // La scala resta quella a 4px di MaeDesign §11: 1rem = 16px per impostazione
  // predefinita del browser, quindi 4px = 0.25rem e i valori coincidono finché
  // nessuno tocca la dimensione di sistema.
  'space-1': '0.25rem',
  'space-2': '0.5rem',
  'space-3': '0.75rem',
  'space-4': '1rem',
  'space-5': '1.5rem',
  'space-6': '2rem',
  'space-7': '3rem',
  'space-8': '4rem',
  'dur-fast': '120ms',
  'dur-base': '200ms',
  'dur-slow': '320ms',
  'ease-standard': 'cubic-bezier(0.4, 0, 0.2, 1)',
  'ease-emphasized': 'cubic-bezier(0.2, 0, 0, 1)',
  'opacity-disabled': '0.45',
};

const LIGHT_SHADOWS = {
  'shadow-soft': '0 10px 26px rgba(0,0,0,0.10)',
  'elev-raised': '0 4px 12px rgba(0,0,0,0.06)',
  'elev-overlay': '0 10px 26px rgba(0,0,0,0.10)',
  'elev-modal': '0 24px 60px rgba(0,0,0,0.18)',
  'state-hover': 'rgba(15,17,21,0.04)',
  'state-pressed': 'rgba(15,17,21,0.09)',
};

const DARK_SHADOWS = {
  'shadow-soft': '0 10px 26px rgba(0,0,0,0.45)',
  'elev-raised': '0 4px 12px rgba(0,0,0,0.40)',
  'elev-overlay': '0 10px 26px rgba(0,0,0,0.45)',
  'elev-modal': '0 24px 60px rgba(0,0,0,0.60)',
  'state-hover': 'rgba(255,255,255,0.06)',
  'state-pressed': 'rgba(255,255,255,0.11)',
};

// ---------------------------------------------------------------------------
// L'insieme completo
// ---------------------------------------------------------------------------

/**
 * Da marca e accento all'insieme dei token, per un modo.
 *
 * @param {string} marca    esadecimale, il colore d'identità
 * @param {string} accento  esadecimale, il colore di supporto
 * @param {'light'|'dark'} modo
 * @returns {Object} nomi senza `--`, valori pronti per `setProperty`
 */
/**
 * L'inchiostro che si legge sopra un fondo: bianco o quasi-nero, il piu' contrastato dei due.
 *
 * ⚠️ Sta qui, esportata, perche' serve in **due** posti che sembrano diversi e non lo sono:
 *
 *  1. il generatore, per scegliere `--on-brand` sul riempimento di marca — il testo sopra un
 *     pieno **non puo'** essere bianco per definizione: MaeDesign fissa `--on-brand: #FFFFFF`, e
 *     su un riempimento chiaro non si legge;
 *  2. i componenti che dipingono su un colore deciso **a runtime** — la tinta di una categoria,
 *     il colore di un tipo di assenza, l'accento scelto dall'azienda. La' un token non basta,
 *     perche' il fondo non e' un token: e' un valore che arriva dai dati.
 *
 * ⚠️ Il secondo caso e' quello che ha fatto nascere questa riga. Quarantaquattro punti mettevano
 * `#fff` sopra a un fondo variabile, e il bianco su un fondo chiaro fa fra 2 e 3:1. Non e' un
 * difetto che si vede provando: si vede solo con **quel dato** — la categoria chiara, l'azienda
 * col colore pallido. Chi prova ne incontra una su dieci.
 *
 * Restituisce un esadecimale, non un `var()`: il chiamante lo mette in uno `style`, e un `var()`
 * calcolato su un fondo che il CSS non conosce non esiste.
 */
export function inkOn(sfondo: string): string {
  /*
   * ⚠️ Un fondo che **non e' un esadecimale** non si misura, e non e' un caso raro: i colori che
   * arrivano dai dati passano spesso per un token (`'var(--brand, #540409)'`), per un `rgba()` o
   * per una sfumatura. `hexToRgb` su quelli lancia, e la prima versione di questa funzione ha
   * fatto cadere quattro prove dell'onboarding — dove il colore dell'azienda, durante la
   * configurazione, e' il token e non ancora una tinta scelta.
   *
   * Si torna al bianco, che e' **esattamente cio' che c'era prima** di questa funzione: nessun
   * punto peggiora. Ma vale sapere che non e' la risposta giusta, e' l'assenza di risposta —
   * quando il fondo e' un token, l'inchiostro e' il suo `--on-*`, e **solo il punto d'uso sa
   * quale**. Una funzione che riceve la stringa `'var(--brand)'` non ha modo di risalire a
   * `--on-brand`: quella e' una scelta di appaiamento, non un calcolo.
   */
  if (!/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(sfondo.trim())) return '#FFFFFF';
  return contrast('#FFFFFF', sfondo) >= contrast(LIGHT_NEUTRAL['ink-900'], sfondo)
    ? '#FFFFFF'
    : LIGHT_NEUTRAL['ink-900'];
}

export function tokenDrift(marca: string, accent: string, mode: Mode = 'light'): Token {
  const dark = mode === 'dark';
  const neutral = dark ? DARK_NEUTRAL : LIGHT_NEUTRAL;
  const superficie = neutral.surface;
  const fondo = neutral.bg;

  // Riempimento di marca. Il colore d'identità e il colore-ruolo sono due cose
  // diverse: `brand` resta sempre quello del Brand Manual, `brand-fill` è la
  // superficie piena e deve staccarsi dallo sfondo (3:1). Per il bordeaux la
  // ricerca non sposta nulla — vale 15:1 su bianco — ma un marchio chiaro
  // (pensa a un giallo) come bottone su fondo bianco sarebbe invisibile.
  const fill = dark
    ? brandFillOnDark(marca, superficie)
    : searchContrast(marca, superficie, NON_TEXT_THRESHOLD);

  // Vedi `inchiostroSu`: la scelta e' la stessa, e sta fuori perche' serve anche
  // ai componenti che dipingono su un colore deciso a runtime.
  const chooseTextAbove = inkOn;

  let safeFill = fill;
  let textOnFill = chooseTextAbove(safeFill);

  // ⚠️ «Il piu' contrastato dei due» non e' «abbastanza contrastato».
  //
  // Esiste una banda stretta di colori — pienissimi e a meta' luminosita', come
  // `#F20D0D` — dove **ne' il bianco ne' il quasi-nero** arrivano a 4,5:1: il
  // massimo e' 4,35. La promessa che facciamo («qualunque colore arrivi, il
  // testo sul bottone si legge») li' non era mantenuta, e le prove non se ne
  // accorgevano perche' provavano una manciata di colori scelti da noi.
  //
  // Quando succede si sposta il riempimento del minimo indispensabile, come per
  // la barra dell'applicazione. Costa un punto di luminosita' su pochi colori e
  // rende vera una garanzia che altrimenti era quasi vera.
  if (contrast(textOnFill, safeFill) < TEXT_THRESHOLD) {
    safeFill = searchContrast(
      safeFill,
      textOnFill,
      TEXT_THRESHOLD
    );
    textOnFill = chooseTextAbove(safeFill);
  }

  /*
   * Il secondo capo della sfumatura di marca.
   *
   * ⚠️ Nasce da quattordici punti che mettevano `#fff` sopra a
   * `linear-gradient(135deg, var(--brand), var(--accent))` — i tondi con le iniziali. Misurato sui
   * due temi e su due aziende, quel bianco cade in **tre casi su quattro**: `--on-brand` sopra
   * `--accent` fa 2,23:1, perche' marca e accento possono stare sui due lati opposti della
   * luminosita' e **nessun inchiostro unico li copre entrambi**.
   *
   * Una sfumatura fra due colori **indipendenti** non e' garantibile, e non serve provarci. Una
   * fra `--brand-fill` e un capo derivato **spostandosi lontano dall'inchiostro** lo e' per
   * costruzione: se l'inchiostro e' bianco il secondo capo e' piu' scuro, se e' quasi-nero e'
   * piu' chiaro. In entrambi i casi il contrasto sul secondo capo puo' solo essere **migliore**
   * di quello sul primo, che e' gia' garantito a 4,5.
   *
   * Il 22% e' quanto basta a far leggere la sfumatura come una sfumatura senza allontanarsi dal
   * colore scelto: sotto il 15% non si distingue da un pieno, sopra il 30% il tondo sembra di due
   * colori diversi.
   */
  const lightInk = textOnFill === '#FFFFFF';
  const fadedBrand = adapt(safeFill, { lMul: lightInk ? 0.78 : 1.22 });

  // L'hover deve **migliorare** o almeno non peggiorare la leggibilità: in
  // MaeDesign il `brand-fill-hover` scuro (#CE525B) scendeva a 4,21:1 col
  // bianco sopra, cioè al passaggio del mouse il testo si leggeva peggio.
  // Su fondo chiaro si scurisce, su fondo scuro si schiarisce, e in entrambi i
  // casi si verifica che il testo sopra continui a leggersi.
  const hoverFill = searchContrast(
    adapt(safeFill, {
      lMul: dark ? 1.08 : 0.78,
      sMul: dark ? 1 : 1.05,
    }),
    textOnFill,
    TEXT_THRESHOLD
  );

  // Il colore dei link: qui la formula fissa di MaeDesign falliva (3,69:1).
  // In chiaro si parte dall'accento scurito e si cerca fino a 4,5:1; in scuro
  // l'accento chiaro di solito passa già, ma la ricerca lo garantisce anche per
  // un accento white-label che non passerebbe.
  const link = searchContrast(
    dark ? accent : adapt(accent, { lMul: 0.76, sMul: 0.7 }),
    [fondo, superficie, neutral['surface-2']],
    TEXT_THRESHOLD
  );

  // L'anello di focus è un indicatore, non testo: 3:1 contro il fondo. La
  // velatura al 45% di MaeDesign valeva 1,40:1 — invisibile a chi naviga da
  // tastiera. Qui è un colore pieno, cercato.
  const focusRing = searchContrast(accent, fondo, NON_TEXT_THRESHOLD);

  const brandTint = dark
    ? `rgba(${Object.values(hexToRgb(adapt(marca, { lMul: 1.55 }))).join(
        ','
      )},0.28)`
    : adapt(marca, { l: 94, s: Math.min(50, rgbToHsl(hexToRgb(marca)).s) });

  const accentTint = dark
    ? `rgba(${Object.values(hexToRgb(accent)).join(',')},0.16)`
    : adapt(accent, { l: 96, sMul: 0.9 });

  // Il testo di marca su fondo scuro non può essere la marca: va schiarito
  // finché non si legge. In chiaro è la marca stessa.
  //
  // ⚠️ Schiarire **e basta** è sbagliato: il bordeaux ha saturazione 91%, e
  // portarlo a piena saturazione fin sopra la soglia produce un rosso acceso da
  // insegna, non un rosato. MaeDesign §5 indica per questo ruolo `#E3A7AB`, che
  // è la marca schiarita **e smorzata**. Il tetto di saturazione è ciò che
  // trasforma la ricerca in un colore che appartiene ancora all'identità.

  /* I fondi su cui un inchiostro di testo capita davvero. In chiaro i tenui sono colori pieni;
     in scuro sono velature, e una velatura non ha un contrasto finché non la si posa — per
     questo si compone sulla superficie prima di misurarla. */
  const composed = (value: string): string => {
    const m = /^rgba?\(([^)]+)\)$/.exec(value);
    if (!m) return value;
    const [r, g, b, a = 1] = m[1].split(',').map(Number);
    return composeOnBackground({ r, g, b, a }, superficie);
  };
  const onBrand = [superficie, neutral['surface-2'], composed(brandTint)];
  const onAccent = [superficie, neutral['surface-2'], composed(accentTint)];

  const brandText = searchContrast(
    dark
      ? adapt(marca, { s: Math.min(rgbToHsl(hexToRgb(marca)).s, 50) })
      : marca,
    onBrand,
    TEXT_THRESHOLD
  );

  /**
   * Il colore **secondario** usato come inchiostro, gemello di `brand-text`.
   *
   * ⚠️ Non e' `accent-dark`, e la differenza si vede su un accento gia' scuro.
   *
   * `accent-dark` e' il colore dei **collegamenti**: parte dall'accento e lo
   * scurisce di un terzo (`lMul: 0.62`), perche' un link deve staccarsi dal
   * testo del corpo. Su un accento medio — l'azzurro — produce il blu giusto.
   * Su un accento gia' scurissimo come `#540409` produce `#370C0F`, che non e'
   * piu' bordeaux: e' nero, e chi ha scelto quel secondario non lo riconosce.
   *
   * Qui invece si parte dall'accento **com'e'** e lo si tocca solo se non
   * regge 4,5:1 sulla superficie. Su `#540409` non lo tocca affatto (15:1); su
   * un azzurro pallido lo scurisce quel tanto che serve.
   *
   * Il tetto di saturazione in scuro e' lo stesso di `brand-text`: schiarire a
   * saturazione piena produce insegne al neon, non inchiostro.
   */
  const accentText = searchContrast(
    dark
      ? adapt(accent, { s: Math.min(rgbToHsl(hexToRgb(accent)).s, 50) })
      : accent,
    onAccent,
    TEXT_THRESHOLD
  );

  // ---------------------------------------------------------------------
  // I derivati del redesign web
  // ---------------------------------------------------------------------
  //
  // Non stanno in `MaeDesign/tokens.css`: sono l'estensione additiva
  // dell'handoff «MaeDesign — Redesign Web». Li teniamo con quei nomi e quei
  // ruoli, perche' e' il vocabolario con cui il dato arriva.
  //
  // `--header-app` in `MaeDesign/tokens.css` e' fisso a `#E9F0F7`. Qui e' **una
  // superficie velata di marca**, e cambia col modo.
  //
  // ⚠️ Ci sono volute **quattro** versioni per arrivarci. Le tre precedenti
  // erano tutte difendibili, e vale ricordarle perche' la quarta si capisce solo
  // sapendo cosa risolve.
  //
  // 1. Veniva dall'**accento**. Sull'azienda di casa sembrava giusto — bordeaux
  //    primario, azzurro secondario, e una barra azzurra corrispondeva al
  //    riferimento — ma corrispondeva per caso: invertendo i due campi la
  //    testata mostrava il secondario.
  //
  // 2. Poi dal primario **schiarito** (o scurito in scuro) a una luminosita'
  //    fissa: assomigliava al colore scelto senza esserlo, come un velo messo
  //    sopra senza ragione.
  //
  // 3. Poi `marca` **tale e quale**, con l'argomento «il colore l'ha scelto
  //    qualcuno, e va mostrato». E' quella che e' durata, ed e' caduta per una
  //    conseguenza che nessuno aveva contato: una barra che **non cambia col
  //    modo** rende impossibile far seguire il tema a cio' che le sta sopra.
  //    L'inchiostro della barra e' il piu' contrastato fra bianco e quasi-nero
  //    su *quel* colore, quindi su una marca celeste resta quasi-nero anche in
  //    tema scuro: nome, email, glifi e lente restavano neri su una pagina
  //    nera-intorno. Chi provava a correggerli con l'inchiostro della *pagina*
  //    otteneva bianco su celeste, **1,9:1** — sotto soglia. Non e' una scelta
  //    fra due gusti: con la barra ferma, una delle due proprieta' — «segue il
  //    tema» e «si legge» — deve cedere.
  //
  // 4. Quindi la barra e' una **superficie**: bianco velato di marca in chiaro,
  //    quasi-nero velato di marca in scuro. L'identita' resta — la velatura e'
  //    la tinta dell'azienda, e il logo e gli accenti sono suoi — ma il *ruolo*
  //    del colore cambia: da fondo pieno a tono. E' anche cio' che fa il gemello
  //    React, dove tutta la testata deriva da
  //    `var(--app-header-fg, var(--ink-900))` su una barra di superficie.
  //
  // Il conto, su questa azienda (`#77A2C5`): chiaro `#F1F6F9` con inchiostro
  // `#0F1115` a **17,4:1**; scuro `#232A34` con `#FFFFFF` a **14,5:1**. Prima
  // era 7,0:1 in entrambi. Si guadagna anche in leggibilita', non solo in
  // coerenza.
  //
  // ⚠️ **Un'azienda puo' ancora avere la barra colorata**, e non passa da qui:
  // `uiThemeJson` con un guscio proprio sovrascrive `--app-header-bg` e
  // `--app-header-fg` in `applica()`. Quella e' una scelta dichiarata dal
  // cliente; questo e' il valore predefinito.
  //
  // ⚠️ **La settima versione, ed e' la terza tornata al punto di partenza.**
  // La barra e' `marca` tale e quale, in **tutti e due i modi**. Il colore che
  // l'azienda ha scelto e' quello, e non si tocca.
  //
  // Le sei versioni precedenti, perche' la storia e' l'unica cosa che impedisce
  // di rifarle:
  //
  //  1. dall'**accento**: sull'azienda di casa sembrava giusto per caso —
  //     bordeaux primario, azzurro secondario — e invertendo i due campi la
  //     testata mostrava il secondario;
  //  2. dal primario **schiarito a una luminosita' fissa**: assomiglia al colore
  //     scelto senza esserlo;
  //  3. `marca` tale e quale — questa;
  //  4. **miscela nel bianco al 10%**: su una marca chiara spariva (`#77A2C5`
  //     dava `#F1F6F9`, cioe' bianco);
  //  5. **tinta costruita a luminosita' 92**: una tinta c'era e i numeri erano
  //     buoni (saturazione 37, contrasto 15,7:1), ma accanto a una pagina bianca
  //     si legge bianca lo stesso;
  //  6. **pieno in chiaro, tinta in scuro**: l'identita' tornava dove si guarda
  //     tutto il giorno, ma in scuro il colore dell'azienda si scuriva — e
  //     scurire il colore di qualcuno resta cambiarglielo.
  //
  // Le versioni 4, 5 e 6 cercavano tutte la stessa cosa: far **seguire il tema**
  // a cio' che sta sopra la barra. Quel desiderio e' legittimo e ha una
  // conseguenza che ora e' una scelta dichiarata, non una svista:
  //
  // ⚠️ **La testata non segue il tema, e non puo'.** L'inchiostro della barra e'
  // il piu' contrastato fra bianco e quasi-nero *su quel colore*, quindi su una
  // marca celeste resta quasi-nero anche in tema scuro. Nome, email, glifi e
  // lente restano scuri su una barra celeste mentre la pagina intorno e' nera.
  // Non e' un difetto da correggere con l'inchiostro della **pagina**: quello
  // darebbe bianco su celeste, **1,9:1**, sotto la soglia. Con la barra ferma,
  // fra «segue il tema» e «si legge» non si puo' avere tutt'e due, e si e'
  // scelto il colore dell'azienda.
  //
  // L'unico ritocco ammesso resta quello che rende **leggibile il testo sopra**:
  // fra bianco e quasi-nero si prende il piu' contrastato, e se nemmeno quello
  // arriva a 4,5:1 — succede in una banda stretta di colori pienissimi a meta'
  // luminosita', tipo `#F20D0D`, dove il massimo e' 4,35:1 — si sposta la barra
  // del minimo indispensabile. Su `#77A2C5` non si sposta di un punto.
  let appBar = marca;
  let barText = chooseTextAbove(appBar);
  // La rete di sicurezza resta, anche se con una barra velata al 10% non scatta
  // per nessun colore: costa nulla e il giorno che qualcuno alza il velo o
  // cambia la superficie, e' l'unica cosa fra quel cambio e un testo
  // illeggibile.
  if (contrast(barText, appBar) < TEXT_THRESHOLD) {
    // `cercaContrasto` adatta il **primo** argomento contro il secondo, e il
    // contrasto e' simmetrico: passandogli la barra come partenza e il testo
    // come fondo, e' la barra a muoversi.
    appBar = searchContrast(appBar, barText, TEXT_THRESHOLD);
    barText = chooseTextAbove(appBar);
  }

  /*
   * Il fondo della colonna del menu: **la superficie della pagina, in tutti e due i modi**. Sta
   * qui e non dentro all'oggetto dei token perche' lo leggono in tre — fondo, inchiostro e voce
   * attiva — e il difetto da cui nasce questa riga e' proprio che i tre si erano scollegati.
   */
  const columnBackground = superficie;

  // Il testo su fondo azzurro tenue. Cercato, non calcolato: deve reggere sia
  // sulla superficie sia dentro il proprio fondo.
  const infoText = searchContrast(
    dark
      ? adapt(accent, { lMul: 1.15 })
      : adapt(accent, { lMul: 0.62, sMul: 0.9 }),
    superficie,
    TEXT_THRESHOLD
  );

  /*
   * I sette colori d'identita' e i loro inchiostri.
   *
   * ⚠️ Il primo gradino **e'** `brand-fill`, non un valore nuovo: cosi' la cosa
   * di marca prende il colore dell'azienda. Gli altri sei sono fissi.
   *
   * ⚠️ Gli inchiostri sono **calcolati**, uno per uno, con la stessa funzione che
   * sceglie l'inchiostro di `brand-fill`. Scriverli a mano e' il difetto che
   * questa meta' della famiglia esiste per chiudere: `--on-cat-6` e' bianco in
   * chiaro e quasi-nero in scuro, perche' il mattone si schiarisce passando al
   * tema scuro. Un `color: '#fff'` cablato e' giusto in un tema e sbagliato
   * nell'altro, e chi l'ha scritto ha visto solo il tema in cui lavorava.
   *
   * Una famiglia di riempimenti **senza** i suoi inchiostri e' lo stesso buco un
   * piano piu' sotto: chi ha bisogno dell'inchiostro se lo scrive a mano.
   */
  const categories: Record<string, string> = {
    'cat-1': safeFill,
    ...(dark ? DARK_CATEGORIES : LIGHT_CATEGORIES),
  };
  const categoryInks = Object.fromEntries(
    Object.entries(categories).map(([nome, full]) => [`on-${nome}`, inkOn(full)]),
  );

  return {
    // identità
    brand: marca,
    accent: accent,
    'brand-dark': adapt(marca, { lMul: 0.72, sMul: 1.05 }),
    'brand-soft': adapt(marca, { lMul: 1.55, sMul: 0.95 }),
    'brand-tint': brandTint,
    'brand-text': brandText,
    'accent-text': accentText,
    'brand-fill': safeFill,
    'brand-grad-2': fadedBrand,
    'brand-fill-hover': hoverFill,
    'on-brand': textOnFill,
    'accent-dark': link,
    'accent-tint': accentTint,

    // neutri, stato, grafici
    ...neutral,
    ...(dark ? DARK_STATUS : LIGHT_STATUS),
    ...(dark ? DARK_CHARTS : LIGHT_CHARTS),
    ...categories,
    ...categoryInks,

    // forma e movimento
    ...SHAPE,
    ...(dark ? DARK_SHADOWS : LIGHT_SHADOWS),

    /**
     * Alias di compatibilità con le variabili che Bootstrap 4 emette in
     * `:root` (`--primary`, `--success`, `--warning`, `--danger`, …).
     *
     * Non è un vezzo: nei nostri fogli ci sono **16 letture di
     * `var(--primary)` in 4 file** — `timesheet`, `icon-picker` e altri —
     * scritte da chi si aspettava una variabile tematizzabile. Oggi cadono
     * sull'azzurro compilato di ArchitectUI; emettendo l'alias seguono il
     * colore dell'azienda senza toccare una riga di quei file.
     *
     * Il servizio scrive i token nello `style` inline di `<html>`, che ha
     * precedenza sulla regola `:root` di Bootstrap: l'alias vince ovunque.
     *
     * ⚠️ `--success`, `--warning` e `--danger` (emessi più sopra) scavalcano
     * per lo stesso motivo gli omonimi di Bootstrap, **ma non hanno lo stesso
     * ruolo**: i nostri sono tarati come colori di *testo* — il giallo è un
     * ambra scuro leggibile, non il `#f7b924` di Bootstrap — quindi
     * `background: var(--warning)` darebbe un fondo bruno. Per i fondi tenui
     * esistono `--success-bg`, `--warning-bg`, `--danger-bg`.
     */
    primary: safeFill,

    // interazione
    'focus-ring': `0 0 0 3px ${focusRing}`,
    'focus-ring-color': focusRing,
    'state-selected': accentTint,

    // derivati del redesign web (vedi sopra)
    'header-app': appBar,
    // L'inchiostro che si legge sulla barra. Qui e' un **derivato**, non una
    // scelta: la barra ora e' un colore pieno, e il grigio del corpo non ci sta
    // sopra per costruzione. Se l'azienda configura un guscio proprio in
    // `uiThemeJson`, `applica()` lo sovrascrive col tono risolto di li'.
    'app-header-fg': barText,

    /*
     * La colonna del menu, che **non e' la barra**: e' una superficie, chiara col tema chiaro e
     * scura col tema scuro.
     *
     * ## Come ci si e' arrivati, in due passaggi
     *
     * Prima del 07-09-2026 la colonna ricadeva sul colore della barra, per fare una cornice a L:
     * `DesktopSidebar` lo diceva — «con lo stesso colore la cornice diventa una L sola» — e
     * nasceva da una segnalazione vera, colonna quasi bianca e barra celeste che si leggevano
     * come due pannelli accostati per sbaglio.
     *
     * Il 07-09 la L si e' rotta **in scuro**: la barra e' `marca` tale e quale nei due modi (vedi
     * la settima versione qui sopra), e una barra alta 56 punti che non segue il tema e' una
     * cosa, una colonna alta tutto lo schermo e' un'altra — in scuro dava una fascia celeste da
     * cima a fondo accanto a una pagina nera.
     *
     * ⚠️ **Il 25-09-2026 si e' rotta anche in chiaro, e questa volta la ragione non e' un
     * difetto: e' che adesso la scelta ce l'ha l'azienda.** Da `admin/branding` la colonna ha due
     * modi — «colore fisso», e allora e' la tinta scelta in tutti e due i temi, oppure «segue il
     * tema», e allora **non si sceglie nessun colore**. Il secondo modo deve mantenere quello che
     * promette: chiaro col tema chiaro, scuro col tema scuro. Finche' in chiaro valeva `appBar`,
     * «segue il tema» consegnava la **marca** — su questa azienda `#77A2C5` — cioe' un colore, ed
     * e' arrivato come segnalazione: «non si sceglie alcun colore» e invece uno ne arrivava.
     *
     * La L non e' persa, e' diventata **una scelta**: chi la vuole mette barra e colonna sullo
     * stesso colore fisso, e ce l'ha anche di notte, che prima non era possibile.
     *
     * ⚠️ Tre token e non uno, perche' cambiando il fondo cambiano **tutti** e tre gli strati:
     *
     *   - il fondo, ovviamente;
     *   - l'inchiostro: sulla barra e' `testoBarra` (il piu' contrastato fra bianco e quasi-nero
     *     *su quel colore*), sulla superficie e' l'inchiostro della pagina. Scambiarli e' il
     *     difetto al rovescio: bianco su celeste fa **2,71:1** su `#77A2C5`;
     *   - la voce **attiva**, che era `marca`: su una superficie scura la marca fa **1,12:1**,
     *     cioe' spariva. `cercaContrasto` la schiarisce del minimo per arrivare a 4,5 — sul
     *     bordeaux del foglio da `#F63A46` a 4,51:1 — quindi resta il colore dell'azienda, non un
     *     accento preso da un'altra parte.
     */
    'sidebar-app': columnBackground,
    /* L'inchiostro della **pagina** nei due modi, perche' ora il fondo e' la pagina nei due modi.
       Era `scuro ? inchiostro : testoBarra`, e il ramo chiaro serviva alla colonna-barra. */
    'on-sidebar-app': neutral['ink-900'],
    /*
     * ⚠️ Cercata **in entrambi i modi**, e la ragione l'ha trovata una prova, non un ragionamento.
     * La prima versione era `scuro ? cercaContrasto(...) : marca`: in scuro andava, in chiaro no —
     * perche' allora in chiaro il fondo della colonna **era `marca`**, quindi la voce attiva era
     * marca su marca, `1:1`. Non era un difetto nuovo: era quello che c'era prima (`mw.brand` sopra
     * `--header-app`), e si vedeva solo su un'azienda **senza** guscio configurato, cioe' su
     * nessuna di quelle che si guardano tutti i giorni.
     *
     * `cercaContrasto` sposta la luminosita' del minimo indispensabile: la tinta resta quella
     * dell'azienda, e la voce attiva si distingue per chiarezza invece che per colore.
     */
    'sidebar-attivo': searchContrast(marca, columnBackground, TEXT_THRESHOLD),
    'surface-alt': dark ? '#1E2129' : '#FCFBFA',
    info: infoText,
    'info-bg': accentTint,
    'success-hover': adapt(
      dark ? DARK_STATUS.success : LIGHT_STATUS.success,
      { lMul: 0.86 }
    ),
    'danger-hover': adapt(dark ? DARK_STATUS.danger : LIGHT_STATUS.danger, {
      lMul: 0.86,
    }),
    'warning-line': dark ? 'rgba(232,206,150,0.35)' : '#E8CE96',

    /**
     * ⚠️ **Il riempimento d'avvertimento, e prima non c'era.**
     *
     * L'insieme sapeva esprimere un pieno per il buon esito
     * (`--success-hover`) e uno per l'errore (`--danger-hover`), e per
     * l'avvertimento **no**: c'erano solo `--warning` (ambra scuro, un colore
     * di *testo*), `--warning-bg` (velatura) e `--warning-line` (il filo di un
     * riquadro). Chi voleva un pallino giallo pieno — la barra della
     * configurazione — non trovava niente di adatto e prendeva
     * `--warning-line`: in chiaro un sabbia slavato, in scuro
     * `rgba(...,0.35)`, cioe' **trasparente**. Un pallino d'avviso che si vede
     * appena.
     *
     * Non e' stata distrazione di chi l'ha scritto: e' un buco della tavolozza,
     * e un buco in una tavolozza si riempie sempre con il token sbagliato piu'
     * vicino. Ora c'e' il pieno, e la coppia col suo inchiostro e' verificata
     * come tutte le altre (vedi `verificaContrasti`).
     *
     * L'ambra e' saturo di proposito — vicino al giallo che ArchitectUI usa da
     * sempre — perche' un avvertimento che non si distingue da uno sfondo non
     * avverte. Con l'inchiostro quasi-nero rende 10,7:1 in chiaro e 8,5:1 in
     * scuro.
     */
    'warning-fill': dark ? '#E0A33B' : '#F7B924',
    'on-warning': '#0F1115',
    disabled: dark ? '#4A4E56' : '#C9CCD2',
    'chart-plan': accentTint,
    'chart-plan-line': adapt(accent, { l: dark ? 34 : 82 }),
    'chart-projection': dark ? '#31343A' : '#E5E6EA',
  };
}

/**
 * Le coppie che devono rispettare una soglia, con il verdetto.
 *
 * È l'audit reso eseguibile: le stesse coppie che il 26/08 abbiamo misurato a
 * mano su MaeDesign, così che un tema white-label non possa ripresentare gli
 * stessi difetti senza che nessuno se ne accorga. Il Theme Studio ne controlla
 * nove e non guarda bordi, focus, placeholder, giallo, stato dentro il proprio
 * sfondo, serie dei grafici e zebra — cioè esattamente le categorie in cui i
 * difetti sono usciti.
 *
 * Le velature vengono composte sul proprio fondo prima della misura.
 */
export function checkContrasts(token: Token): Check[] {
  const pieno = (value: string, fondo: string) => {
    const m = String(value).match(/rgba?\(([^)]+)\)/);
    if (!m) return value;
    const [r, g, b, a = 1] = m[1].split(',').map(Number);
    return composeOnBackground({ r, g, b, a }, fondo);
  };

  const superficie = token.surface;
  const fondo = token.bg;

  const pairs: Array<[string, string, string, number]> = [
    ['testo principale', token['ink-900'], superficie, TEXT_THRESHOLD],
    ['testo del corpo', token['ink-700'], superficie, TEXT_THRESHOLD],
    ['testo secondario', token['ink-500'], superficie, TEXT_THRESHOLD],
    [
      'testo secondario su superficie alternata',
      token['ink-500'],
      token['surface-2'],
      TEXT_THRESHOLD,
    ],
    ['segnaposto dei campi', token['ink-300'], superficie, TEXT_THRESHOLD],
    /* ⚠️ Un inchiostro non capita su un fondo solo, ed e' il modo in cui `#717781` era passato
       da conforme: 4,51 su bianco, 4,17 su `surface-2`. La stessa lezione e' scritta in testa a
       `maedesign-tokens.css` per `accent-text`, e questa riga la applica. */
    ['segnaposto su superficie alternata', token['ink-300'], token['surface-2'], TEXT_THRESHOLD],
    ['link', token['accent-dark'], fondo, TEXT_THRESHOLD],
    [
      'testo sul riempimento di marca',
      token['on-brand'],
      token['brand-fill'],
      TEXT_THRESHOLD,
    ],
    [
      'testo di marca sulla superficie',
      token['brand-text'],
      superficie,
      TEXT_THRESHOLD,
    ],
    /* ⚠️ **Le cinque righe che mancavano, e mancavano dove fa piu' male.** Gli esiti fissi —
       positivo, avvertimento, errore — erano gia' provati «nel proprio avviso», cioe' sul tenue
       su cui capitano davvero. Marca e accento no: erano provati sulla sola superficie bianca.
       E sono gli unici due colori che **sceglie l'azienda**, quindi gli unici che possono
       arrivare da un valore che nessuno qui ha mai visto. `mw.brandText` su `mw.brandTint` e'
       una coppia che si rende — le pastiglie di `tenants/$tenantId`, `licenses-catalog`,
       `tenant-attributes`, `MobileHome` — e su 72 colori di marca provati ne falliva **49**. */
    [
      'testo di marca su superficie alternata',
      token['brand-text'],
      token['surface-2'],
      TEXT_THRESHOLD,
    ],
    [
      'testo di marca sul proprio tenue',
      token['brand-text'],
      pieno(token['brand-tint'], superficie),
      TEXT_THRESHOLD,
    ],
    [
      'testo d’accento sulla superficie',
      token['accent-text'],
      superficie,
      TEXT_THRESHOLD,
    ],
    [
      'testo d’accento su superficie alternata',
      token['accent-text'],
      token['surface-2'],
      TEXT_THRESHOLD,
    ],
    [
      'testo d’accento sul proprio tenue',
      token['accent-text'],
      pieno(token['accent-tint'], superficie),
      TEXT_THRESHOLD,
    ],
    [
      'testo sul riempimento in hover',
      token['on-brand'],
      token['brand-fill-hover'],
      TEXT_THRESHOLD,
    ],
    ['esito positivo', token.success, superficie, TEXT_THRESHOLD],
    [
      'esito positivo nel proprio avviso',
      token.success,
      pieno(token['success-bg'], superficie),
      TEXT_THRESHOLD,
    ],
    ['avvertimento', token.warning, superficie, TEXT_THRESHOLD],
    [
      'avvertimento nel proprio avviso',
      token.warning,
      pieno(token['warning-bg'], superficie),
      TEXT_THRESHOLD,
    ],
    // La coppia del pieno d'avvertimento: `--on-warning` sopra
    // `--warning-fill`. Senza questa riga il pieno sarebbe l'unico colore
    // pieno dell'insieme a non avere una garanzia sul testo che ci va sopra.
    [
      'testo sul pieno di avvertimento',
      token['on-warning'],
      token['warning-fill'],
      TEXT_THRESHOLD,
    ],
    ['errore', token.danger, superficie, TEXT_THRESHOLD],
    [
      'errore nel proprio avviso',
      token.danger,
      pieno(token['danger-bg'], superficie),
      TEXT_THRESHOLD,
    ],
    [
      'contorno dei controlli',
      pieno(token['line-strong'], superficie),
      superficie,
      NON_TEXT_THRESHOLD,
    ],
    ['anello di focus', token['focus-ring-color'], fondo, NON_TEXT_THRESHOLD],
    [
      'riempimento di marca sulla superficie',
      token['brand-fill'],
      superficie,
      NON_TEXT_THRESHOLD,
    ],
    ['serie 1 del grafico', token['chart-1'], superficie, NON_TEXT_THRESHOLD],
    ['serie 2 del grafico', token['chart-2'], superficie, NON_TEXT_THRESHOLD],
    ['serie 3 del grafico', token['chart-3'], superficie, NON_TEXT_THRESHOLD],
    // I sette colori d'identita' si vedono da soli sul fondo: l'unico vincolo
    // che la tavolozza puo' garantire e' che si stacchino da quello.
    ...[1, 2, 3, 4, 5, 6, 7].map(
      (n): [string, string, string, number] => [
        `colore d'identita' ${n}`, token[`cat-${n}`], superficie, NON_TEXT_THRESHOLD,
      ],
    ),
  ];

  return pairs.map(([nome, text, sfondo, soglia]) => {
    const rapporto = contrast(text, sfondo);
    return {
      nome,
      text,
      sfondo,
      threshold: soglia,
      report: Math.round(rapporto * 100) / 100,
      outcome: judgement(rapporto, soglia),
    };
  });
}

// ---------------------------------------------------------------------------
// Il guscio scelto dall'azienda
// ---------------------------------------------------------------------------

/**
 * Risolve una superficie del guscio (testata o menu) da quanto l'azienda ha
 * scelto in `tenants.uiThemeJson`, **garantendo che il testo sopra si legga**.
 *
 * ## Perché non ci si limita a obbedire
 *
 * L'applicazione ingenua di quel blob sarebbe questa:
 *
 *     function toneColor(tone) {
 *       return tone === 'light' ? 'rgba(255,255,255,0.95)' : null;
 *     }
 *
 * Bianco se la scelta è «chiara», **niente** se è «scura» — e nessuna verifica
 * che quel testo si legga sul fondo scelto. Le conseguenze sono due, entrambe
 * raggiungibili da un pannello di configurazione:
 *
 * - testata bordeaux medio con tonalità «scura»: l'inchiostro predefinito su un
 *   fondo scuro, cioè una barra illeggibile;
 * - testata chiara con tonalità «chiara»: bianco su quasi-bianco.
 *
 * Qui la tonalità dichiarata è una **preferenza, non un ordine**: si onora
 * quando regge la soglia, altrimenti si usa l'altra e lo si dichiara nel
 * risultato. Chi ha scelto male ottiene comunque un'interfaccia leggibile, e
 * chi ha scelto bene non si accorge di niente.
 *
 * ## Le sfumature si misurano su entrambi gli estremi
 *
 * Con `sfumatura` attiva il fondo passa da un colore all'altro, e il testo deve
 * leggersi **per tutta la barra**. Il conto si fa quindi sul caso peggiore dei
 * due estremi, non sul primo colore: un gradiente da bordeaux a rosa chiaro
 * regge il bianco a sinistra e lo perde a destra.
 *
 * @param {Object} spec  `{ colore, colore2, sfumatura, angolo, tono }`
 * @param {string} inchiostroScuro  il testo scuro da usare come alternativa
 * @returns {null|{sfondo, testo, tonoRichiesto, tonoUsato, corretto, rapporto}}
 *          `null` se l'azienda non ha scelto niente: nessuno scavalcamento.
 */
export interface ShellSpec {
  color?: string | null;
  color2?: string | null;
  shade?: boolean | null;
  corner?: number | string | null;
  tone?: string | null;
}
export interface ResolvedShell {
  background: string;
  text: string;
  requiredTone?: string | null;
  usedTone?: string | null;
  correct: boolean;
  adapted?: boolean;
  originalEndpoints?: string[];
  report: number;
}

export function resolveShellSurface(
  spec: ShellSpec | null | undefined,
  darkInk: string = LIGHT_NEUTRAL['ink-900']
): ResolvedShell | null {
  const valid = (c: unknown): c is string =>
    typeof c === 'string' &&
    /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(c.trim());
  const norm = (c: string) => (c.trim().startsWith('#') ? c.trim() : `#${c.trim()}`);

  if (!spec || !valid(spec.color)) return null;

  const c1 = norm(spec.color as string);
  const withShade = Boolean(spec.shade) && valid(spec.color2);
  const c2 = withShade ? norm(spec.color2 as string) : null;
  const endpoints = c2 ? [c1, c2] : [c1];

  // Il caso peggiore di ogni candidato lungo tutta la superficie.
  const worst = (testo: string) =>
    Math.min(...endpoints.map((f) => contrast(testo, f)));

  const light = {
    tone: 'light',
    text: '#FFFFFF',
    report: worst('#FFFFFF'),
  };
  const scuro = {
    tone: 'dark',
    text: darkInk,
    report: worst(darkInk),
  };

  const required =
    spec.tone === 'light' ? light : spec.tone === 'dark' ? scuro : null;

  // La preferenza vince se regge; altrimenti vince chi si legge. Senza
  // preferenza si prende semplicemente il migliore dei due.
  let chosen;
  if (required && required.report >= TEXT_THRESHOLD) chosen = required;
  else chosen = light.report >= scuro.report ? light : scuro;

  // Il caso che nessuna tonalità copre: una sfumatura che attraversa mezza
  // scala di luminosità — bordeaux verso rosa chiaro — non regge **nessun**
  // colore di testo, perché quello che si legge a sinistra sparisce a destra.
  //
  // Qui non ci si limita a segnalarlo: si stringe la sfumatura. Ogni estremo
  // che non regge viene spostato a tinta invariata finché non raggiunge la
  // soglia contro il testo scelto. L'intenzione dell'azienda resta — resta una
  // sfumatura, e resta la sua — ma dentro un intervallo leggibile.
  const finalEndpoints = endpoints.map((f) =>
    contrast(chosen.text, f) >= TEXT_THRESHOLD
      ? f
      : searchContrast(f, chosen.text, TEXT_THRESHOLD)
  );
  const adapted = finalEndpoints.some((f, i) => f !== endpoints[i]);

  const corner = Number.isFinite(spec.corner) ? spec.corner : 90;
  const [f1, f2] = finalEndpoints;

  return {
    background: f2 ? `linear-gradient(${corner}deg, ${f1}, ${f2})` : f1,
    text: chosen.text,
    requiredTone: spec.tone ?? null,
    usedTone: chosen.tone,
    correct: Boolean(required) && required?.tone !== chosen.tone,
    adapted,
    originalEndpoints: endpoints,
    report:
      Math.round(
        Math.min(...finalEndpoints.map((f) => contrast(chosen.text, f))) * 100
      ) / 100,
  };
}
